import { useEffect, useState } from 'react'
import { ky } from './client'
import { getReviewList } from './reviews'
import { createXlsx, formatToDate, withFallbackReportData } from '../util'
import { toast } from 'react-toastify'

export const getSurveys = async () => {
  return await ky.get('feedback/surveys', {}).json()
}

export const useSurveys = () => {
  const [surveys, setSurveys] = useState([])
  const [error, setError] = useState(false)
  const [moduleName, setModuleName] = useState(null)

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      if (cancelled) return

      const {
        successInd,
        data: { surveys, moduleName }
      } = await getSurveys().catch(() => setError(true))

      if (successInd) {
        setSurveys(surveys)
        setModuleName(moduleName)
        setError(false)
      } else {
        setError(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [setSurveys])

  return { surveys, moduleName, error }
}

export const submitSurvey = ({ assignmentId, type }, data) => {
  return ky.put(`feedback/surveys/${type}/${assignmentId}`, { json: data })
}

/**
 * Fetches response data for experiential data.
 */
export const getResponses = async (filters) => {
  return await ky.post('feedback/experiential/responses', { json: filters }).json()
}

/**
 * Fetches reference data for experiental data.
 */
export const getReferences = async () => {
  try {
    const response = await ky.get('feedback/experiential/references', {}).catch(console.error)
    const refs = await response.json()

    // Filters
    const filters = {
      planUploadedDate: { title: 'Plan Upload date', style: 'date' }
    }

    Object.entries(refs).forEach(([key, list]) => {
      /* Makes the title pretty. */
      const prettify = (string) => {
        const split = string.replace(/([A-Z])/g, ' $1')
        return split[0].toUpperCase() + split.slice(1)
      }

      // Maps list fields.
      filters[key] = {
        title: prettify(key),
        style: 'list',
        list: list.map((value) => {
          return { label: value, value }
        })
      }
    })

    return { filters }
  } catch (e) {
    console.log(e)
    return { filters: [] }
  }
}

export const getResponsesExport = async (filters) => {
  try {
    const response = await ky.post('feedback/experiential/export', { json: filters }).catch(console.error)

    const blob = await response.blob()

    // Create blob link to download
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `experiential.xlsx`)

    // Append to html link element page
    document.body.appendChild(link)

    // Start download
    link.click()

    // Clean up and remove the link
    link.parentNode.removeChild(link)
  } catch (e) {
    toast('Could not load data, please try again later.', { type: 'error' })
    console.error(e)
  }
}

/**
 * export links download
 * @param {*} surveys
 * @param {*} search
 * @param {*} finalisedDateSearch
 * @param {*} textCopy - hand different text copy for different modules
 * @param {*} modulename - module name to be used in the file name and custom columns
 * @
 */
export const exportLinks = async (surveys, search, finalisedDateSearch, textCopy, modulename) => {
  // config
  const customColumns = {
    pepId: { label: 'Internal ID' },
    keyStage: { label: 'Key Stage' },
    primaryNeed: { label: 'Primary Need' },
    currentSocialCare: {
      label: 'Current Social Care'
    },
    settingName: { label: 'Setting Name' },
    settingType: { label: 'Setting Type' },
    caseOfficer: { label: textCopy.plans.caseOfficerLabel },
    teamRegion: { label: 'Team Region' },
    finalisedDate: {
      label: textCopy.plans.finalisedDateLabel
    },
    archived: { label: 'Historical' },
    [`${modulename.toLowerCase()}_carer_feedback`]: {
      label: 'Parent/Carer survey links',
      width: 80
    },
    qepa_child_y: { label: 'Younger C/YP survey links', width: 80 },
    qepa_child_o: { label: 'Older C/YP survey links', width: 80 },
    // handle AR formating
    ar_child_feedback_y: { label: 'Younger CY/P survey links', width: 80 },
    ar_child_feedback_o: { label: 'Older CY/P survey links', width: 80 },
    // EHCP
    ehcp_child_longer: { label: 'CYP Feedback (Longer version) survey links', width: 80 },
    ehcp_child_shorter: { label: 'CYP Feedback (Shorter version) survey links', width: 80 }
  }
  try {
    const plans = await getReviewList({
      ...(!!search ? { search } : {}),
      ...(!!finalisedDateSearch ? { finalisedDateSearch } : {})
    })
    // format date
    plans.rows.forEach((plan) => {
      plan.finalisedDate = formatToDate(plan.finalisedDate)
    })

    // map plans to add survey links
    const plansAndSurveys = plans.rows.map((plan) => {
      surveys.forEach((survey) => {
        plan[survey.type] = `${window.location.origin}/feedback/${survey.type}/${plan.id}`
      })
      delete plan.id
      plan.archived = plan.archived ? 'Historical' : 'Current'
      return plan
    })
    createXlsx(plansAndSurveys, `survey_links_${modulename}.xlsx`, {
      customColumns,
      sheetName: 'Survey Links'
    })
  } catch (e) {
    console.error(e)
    // display error
    toast('Could not load data, please try again later.', {
      type: 'error'
    })
  }
}

export const getFeedbackStackedArea = async () => {
  return await withFallbackReportData(({ toast }) => ky.get('feedback/stacked-area', toast('Feedback Survey Completed')).json())
}
